/* ------------------- IMPORTS */
import styled from '@emotion/styled'
import ShortUrl from 'api/ShortUrl'
import Utils from 'api/Utils'
import { Box, Button, Icons } from 'components'
import { openMediaModal } from 'components/Modal/ModalManager'
import { set } from 'date-fns'
import { useLayoutEffect, useRef, useState } from 'react'
import { Media } from 'types'
import { IPostVariant, IUrl } from 'types/PostInterface'
import { AppStore, T, showInfo, showWarning } from 'utils'
import { updateActive, updatePost } from 'utils/PostUtils/Post'
import characterCounter from 'assets/icons/characterCouter.svg'

export const onClickOpenEditMetadataModal = (post: IPostVariant, visibleLink: IUrl, section) => {
  AppStore.openModal({
    id: 'edit-metadata-modal',
    body: <EditMetadataModal visibleLink={visibleLink} post={post} section={section} />,
    style: {
      maxWidth: 440,
    },
  })
}

/* ------------------- INTERFACES  */

interface Props {
  visibleLink: IUrl
  post: IPostVariant
  section: string
}

const MAX_TITLE_HEIGHT = 75
const MIN_TITLE_HEIGHT = 20

export const EditMetadataModal = ({ visibleLink, post, section }: Props) => {
  /* ----------------- VARIABLES  */
  const titleRef = useRef<HTMLTextAreaElement>(null)
  const { metaData } = visibleLink

  const { ogDescription, ogSiteName, ogImg, ogTitle } = metaData
  const [selectedMedia, setSelectedMedia] = useState<Media | null>(null)
  /* ----------------- STATE  */
  const [newMetadata, setNewMetadata] = useState({
    siteName: ogSiteName,
    title: ogTitle,
    description: ogDescription,
    image: ogImg,
  })

  /* ----------------- METHODS  */
  const onChangeInput = (e) => {
    const { value, name } = e.target
    setNewMetadata({ ...newMetadata, [name]: value })
  }

  const handleClickSave = () => {
    if (newMetadata.siteName?.length === 0) return showInfo(T.info.enterName)

    if (newMetadata.title?.length === 0) return showInfo(T.info.enterTitle)

    updateMeta()

    AppStore.closeModal('edit-metadata-modal')
  }

  useLayoutEffect(() => {
    if (!titleRef.current) return
    // Reset height - important to shrink on delete
    titleRef.current.style.height = 'inherit'
    // Set height
    titleRef.current.style.height = `${Math.max(titleRef.current.scrollHeight, MIN_TITLE_HEIGHT)}px`
  }, [newMetadata.title])

  /* ----------------- API CALL  */
  const updateMeta = async () => {
    const modifyedMetaData = {
      ogDescription: newMetadata.description,
      ogSiteName: newMetadata.siteName,
      ogImg: newMetadata.image,
      ogTitle: newMetadata.title,
    }

    try {
      await ShortUrl.modifyMetadata(visibleLink.id, modifyedMetaData)

      if (section === 'preview') {
        const newUrls = post.data.urls?.map((url) => {
          if (url.id === visibleLink.id) {
            return {
              ...url,
              metaData: {
                ...url.metaData,
                ...modifyedMetaData,
              },
            }
          }
          return url
        })

        updateActive({ urls: newUrls })
      }

      if (section === 'extra') {
        const newUrls = post.data.firstComment?.urls?.map((url) => {
          if (url.id === visibleLink.id) {
            return {
              ...url,
              metaData: {
                ...url.metaData,
                ...modifyedMetaData,
              },
            }
          }
          return url
        })

        updatePost(post.variantId, {
          firstComment: {
            ...post.data.firstComment,
            urls: newUrls,
          },
        })
      }
    } catch (e) {
      showWarning(T.warning.problemSettingMetadata)
    }
  }

  /**
   * Attiva la modale per la selezione dell'immagine e l'override dell'immagine originale del link
   */
  const onMetadataImageChange = () => {
    openMediaModal({
      context: 'image',
      onMediaSelected: (media) => {
        uploadMediaPublic(media[0])
      },
      selectedMedias: [],
      closeOnlyMedia: true,
    })
  }

  const uploadMediaPublic = async (media: Media) => {
    const newMedia = await Utils.saveImageRemote(media.url, media.name, media.path, true)
    setNewMetadata({ ...newMetadata, image: newMedia.presignedUrl })
    newMedia && setSelectedMedia(newMedia)
  }

  return (
    <EditMetadataModalContainer>
      <EditMetadataContent>
        <MetadataImage onClick={onMetadataImageChange}>
          <img src={selectedMedia?.url ?? newMetadata.image} />
          <Icons.uploadFile />
        </MetadataImage>

        <MetadataContent>
          <MetadataTitle
            ref={titleRef}
            value={newMetadata.title}
            name="title"
            onChange={(e) => onChangeInput(e)}
            placeholder="Site title"
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              justifyContent: 'end',
              alignItems: 'baseline',
              marginTop: '4px',
            }}
          >
            <Icons.characterCouter
              style={{ maxWidth: 15, maxHeight: 15, marginRight: 4 }}
              fill={AppStore.theme.o.black}
            />
            <div>{newMetadata.title?.length || 0}</div>
          </div>

          <Divider />
          <MedataDescription
            value={newMetadata.description}
            name="description"
            onChange={(e) => onChangeInput(e)}
            placeholder="Site description"
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              justifyContent: 'end',
              alignItems: 'baseline',
              marginTop: 4,
            }}
          >
            <Icons.characterCouter
              style={{ maxWidth: 15, maxHeight: 15, marginRight: 4 }}
              fill={AppStore.theme.o.black}
            />
            <div>{newMetadata.description?.length || 0}</div>
          </div>
        </MetadataContent>
      </EditMetadataContent>

      <EditMetadataSaveButton onClick={handleClickSave} variant="primary">
        {T.settings.save}
      </EditMetadataSaveButton>
    </EditMetadataModalContainer>
  )
}

const EditMetadataModalContainer = styled(Box)`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const EditMetadataContent = styled(Box)`
  padding: 16px;
  display: flex;
  flex-direction: row;
  gap: 8px;
`

const EditMetadataSaveButton = styled(Button)`
  width: 213px;
  min-height: 42px;
  padding: 12px 88px !important;
  position: absolute;
  right: 0px;
  bottom: -50px;
  font-size: 14px;
`

const MetadataImage = styled(Box)`
  margin-top: 8px;
  width: 56px;
  height: 56px;
  border-radius: 12px;
  cursor: pointer;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    border-radius: 12px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    object-fit: cover;
  }

  svg {
    width: 100%;
    height: 100%;
    opacity: 0;
    transition: 0.3s ease-in-out;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &:hover {
    svg {
      border-radius: 12px;
      background-color: rgba(60, 60, 60, 0.7);
      opacity: 1;
      fill: ${() => AppStore.theme.o.white};
    }
  }
`

const MetadataContent = styled(Box)`
  width: 335px;
`

const Divider = styled(Box)`
  width: calc(100%-8px);
  height: 0.5px;
  background-color: ${() => AppStore.theme.o.grey};
  margin: 4px 0px 4px 8px;
`

const MetadataTitle = styled.textarea`
  color: ${() => AppStore.theme.o.black};
  background-color: ${() => AppStore.theme.o.surface};
  border: none;
  border-radius: 14px;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  min-height: ${MIN_TITLE_HEIGHT}px;
  max-height: ${MAX_TITLE_HEIGHT}px;
  padding: 8px;
  transition: 0.3s ease-in-out;
  resize: none;
  box-sizing: border-box;

  &:hover,
  :focus {
    background-color: ${() => AppStore.theme.o.lightGrey};
  }

  &::-webkit-scrollbar {
    display: none;
  }
`

const MedataDescription = styled.textarea`
  color: ${() => AppStore.theme.o.black};
  background-color: ${() => AppStore.theme.o.surface};
  border: none;
  border-radius: 14px;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  padding: 8px;
  min-height: 72px;
  resize: none;
  transition: 0.3s ease-in-out;

  &:hover,
  :focus {
    background-color: ${() => AppStore.theme.o.lightGrey};
  }

  &::-webkit-scrollbar {
    display: none;
  }
`
